import { useDeferredValue, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/components/forms/input'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'
import { ProviderLogo } from '@/features/admin/model-provider/provider-logo'
import { cn } from '@/lib/cn'

export interface CoreAgentModelOption {
  id: string
  label: string
  provider: string
  providerLabel: string
}

interface ModelSelectionDrawerProps {
  models: CoreAgentModelOption[]
  onApply: (modelIds: string[]) => void
  onOpenChange: (open: boolean) => void
  selectedIds: string[]
}

const allProviders = '__all__'

function SearchIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <circle cx="7" cy="7" r="4.25" stroke="currentColor" strokeWidth="1.4" />
      <path d="m10.25 10.25 3 3" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" />
    </svg>
  )
}

function providerKey(model: CoreAgentModelOption) {
  return model.provider.trim().toLocaleLowerCase() || 'other'
}

export function ModelSelectionDrawer({
  models,
  onApply,
  onOpenChange,
  selectedIds,
}: ModelSelectionDrawerProps) {
  const { t } = useTranslation('adminSettings')
  const [activeProvider, setActiveProvider] = useState(allProviders)
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search)
  const [draftSelected, setDraftSelected] = useState<Set<string>>(() => new Set(selectedIds))

  const providers = useMemo(() => {
    const grouped = new Map<string, { count: number; label: string }>()
    for (const model of models) {
      const key = providerKey(model)
      const current = grouped.get(key)
      grouped.set(key, {
        count: (current?.count ?? 0) + 1,
        label: model.providerLabel || model.provider || t('coreAgent.modelPicker.otherProvider'),
      })
    }
    return [...grouped.entries()].sort((left, right) => left[1].label.localeCompare(right[1].label))
  }, [models, t])

  const visibleModels = useMemo(() => {
    const keyword = deferredSearch.trim().toLocaleLowerCase()
    return models.filter((model) => {
      if (activeProvider !== allProviders && providerKey(model) !== activeProvider) {
        return false
      }
      if (!keyword) {
        return true
      }
      return [model.label, model.providerLabel, model.provider].some((value) =>
        value.toLocaleLowerCase().includes(keyword),
      )
    })
  }, [activeProvider, deferredSearch, models])

  return (
    <Drawer
      bodyClassName="min-h-0 flex-1 overflow-hidden"
      footer={
        <>
          <span className="mr-auto text-sm text-[var(--color-text-muted)]">
            {t('coreAgent.modelPicker.selectedCount', { count: draftSelected.size })}
          </span>
          <Button onClick={() => onOpenChange(false)} variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={() => {
              onApply(
                models.filter((model) => draftSelected.has(model.id)).map((model) => model.id),
              )
              onOpenChange(false)
            }}
          >
            {t('coreAgent.modelPicker.apply')}
          </Button>
        </>
      }
      headerAddon={
        <Input
          aria-label={t('coreAgent.modelPicker.searchLabel')}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('coreAgent.modelPicker.searchPlaceholder')}
          prefix={<SearchIcon />}
          value={search}
        />
      }
      onOpenChange={onOpenChange}
      open
      size="lg"
      title={t('coreAgent.modelPicker.title')}
    >
      <div className="grid h-full min-h-0 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] sm:grid-cols-[180px_minmax(0,1fr)] sm:grid-rows-1">
        <nav
          aria-label={t('coreAgent.modelPicker.providerGroups')}
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-[var(--color-divider)] bg-[var(--color-surface-muted)] p-3 sm:block sm:overflow-y-auto sm:border-r sm:border-b-0"
        >
          <button
            className={cn(
              'flex min-h-10 min-w-fit cursor-pointer items-center justify-between gap-3 rounded-md px-3 text-left text-sm transition-colors sm:w-full',
              activeProvider === allProviders
                ? 'bg-white font-semibold text-[var(--color-primary)] shadow-sm'
                : 'text-[var(--color-text-muted)] hover:bg-white hover:text-[var(--color-text)]',
            )}
            onClick={() => setActiveProvider(allProviders)}
            type="button"
          >
            <span>{t('coreAgent.modelPicker.allProviders')}</span>
            <span className="text-xs tabular-nums">{models.length}</span>
          </button>
          {providers.map(([key, provider]) => (
            <button
              className={cn(
                'mt-0 flex min-h-10 min-w-fit cursor-pointer items-center justify-between gap-3 rounded-md px-3 text-left text-sm transition-colors sm:mt-1 sm:w-full',
                activeProvider === key
                  ? 'bg-white font-semibold text-[var(--color-primary)] shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:bg-white hover:text-[var(--color-text)]',
              )}
              key={key}
              onClick={() => setActiveProvider(key)}
              type="button"
            >
              <span className="truncate">{provider.label}</span>
              <span className="text-xs tabular-nums">{provider.count}</span>
            </button>
          ))}
        </nav>

        <div className="min-h-0 overflow-y-auto p-4">
          {visibleModels.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-[var(--color-panel-border)] px-4 text-center text-sm text-[var(--color-text-muted)]">
              {t('coreAgent.modelPicker.empty')}
            </div>
          ) : (
            <div className="grid gap-1">
              {visibleModels.map((model) => {
                const checked = draftSelected.has(model.id)
                return (
                  <label
                    className={cn(
                      'flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors',
                      checked
                        ? 'border-[rgba(25,91,255,0.32)] bg-[rgba(25,91,255,0.05)]'
                        : 'border-transparent hover:bg-[var(--color-surface-muted)]',
                    )}
                    key={model.id}
                  >
                    <input
                      checked={checked}
                      className="size-4 accent-[var(--color-primary)]"
                      onChange={() => {
                        setDraftSelected((current) => {
                          const next = new Set(current)
                          if (next.has(model.id)) {
                            next.delete(model.id)
                          } else {
                            next.add(model.id)
                          }
                          return next
                        })
                      }}
                      type="checkbox"
                    />
                    <ProviderLogo
                      className="size-7 rounded-md p-1"
                      label={model.providerLabel}
                      provider={model.provider}
                      variant="compact"
                    />
                    <span className="min-w-0 truncate text-sm font-medium text-[var(--color-text)]">
                      {model.label}
                    </span>
                  </label>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  )
}
